/**
 * Fullscreen image viewer overlay
 * Opens image(s) in a dark fullscreen overlay with pinch-to-zoom, close controls,
 * and prev/next navigation for multi-image viewing.
 */

export function openImageViewer(imageUrlOrUrls: string | string[], startIndex = 0): void {
  const urls = Array.isArray(imageUrlOrUrls) ? imageUrlOrUrls : [imageUrlOrUrls];
  if (urls.length === 0) return;

  let currentIndex = Math.min(Math.max(0, startIndex), urls.length - 1);

  const overlay = document.createElement('div');
  overlay.className = 'image-viewer-overlay';

  const hasMultiple = urls.length > 1;

  overlay.innerHTML = `
    <img class="image-viewer-img" src="${urls[currentIndex]}" alt="Photo" />
    <button class="image-viewer-close" title="Close">
      <i class="fas fa-times"></i>
    </button>
    ${hasMultiple ? `
      <button class="image-viewer-prev" title="Previous" ${currentIndex === 0 ? 'style="display:none"' : ''}>
        <i class="fas fa-chevron-left"></i>
      </button>
      <button class="image-viewer-next" title="Next" ${currentIndex === urls.length - 1 ? 'style="display:none"' : ''}>
        <i class="fas fa-chevron-right"></i>
      </button>
      <div class="image-viewer-counter">${currentIndex + 1} / ${urls.length}</div>
    ` : ''}
  `;

  document.body.appendChild(overlay);

  const img = overlay.querySelector('.image-viewer-img') as HTMLImageElement;
  const closeBtn = overlay.querySelector('.image-viewer-close') as HTMLButtonElement;
  const prevBtn = overlay.querySelector('.image-viewer-prev') as HTMLButtonElement | null;
  const nextBtn = overlay.querySelector('.image-viewer-next') as HTMLButtonElement | null;
  const counter = overlay.querySelector('.image-viewer-counter') as HTMLElement | null;

  // --- Pinch-to-zoom & pan state ---
  let scale = 1;
  let translateX = 0;
  let translateY = 0;
  let lastDistance = 0;
  let lastCenter = { x: 0, y: 0 };
  let isPanning = false;
  let panStartX = 0;
  let panStartY = 0;
  let startTranslateX = 0;
  let startTranslateY = 0;

  function applyTransform(): void {
    img.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
  }

  function resetTransform(): void {
    scale = 1;
    translateX = 0;
    translateY = 0;
    applyTransform();
  }

  function navigateTo(index: number): void {
    if (index < 0 || index >= urls.length) return;
    currentIndex = index;
    img.src = urls[currentIndex];
    resetTransform();
    updateNavButtons();
  }

  function updateNavButtons(): void {
    if (prevBtn) prevBtn.style.display = currentIndex === 0 ? 'none' : '';
    if (nextBtn) nextBtn.style.display = currentIndex === urls.length - 1 ? 'none' : '';
    if (counter) counter.textContent = `${currentIndex + 1} / ${urls.length}`;
  }

  function getTouchDistance(t1: Touch, t2: Touch): number {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function getTouchCenter(t1: Touch, t2: Touch): { x: number; y: number } {
    return {
      x: (t1.clientX + t2.clientX) / 2,
      y: (t1.clientY + t2.clientY) / 2,
    };
  }

  // --- Swipe detection state ---
  let swipeStartX = 0;
  let swipeStartY = 0;
  let swipeStartTime = 0;

  function onTouchStart(e: TouchEvent): void {
    if (e.touches.length === 2) {
      e.preventDefault();
      lastDistance = getTouchDistance(e.touches[0], e.touches[1]);
      lastCenter = getTouchCenter(e.touches[0], e.touches[1]);
    } else if (e.touches.length === 1) {
      if (scale > 1) {
        // Pan when zoomed in
        isPanning = true;
        panStartX = e.touches[0].clientX;
        panStartY = e.touches[0].clientY;
        startTranslateX = translateX;
        startTranslateY = translateY;
      } else if (hasMultiple) {
        // Track swipe start for navigation
        swipeStartX = e.touches[0].clientX;
        swipeStartY = e.touches[0].clientY;
        swipeStartTime = Date.now();
      }
    }
  }

  function onTouchMove(e: TouchEvent): void {
    if (e.touches.length === 2) {
      e.preventDefault();
      const newDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const newCenter = getTouchCenter(e.touches[0], e.touches[1]);

      const delta = newDistance / lastDistance;
      scale = Math.min(Math.max(scale * delta, 1), 5);

      translateX += newCenter.x - lastCenter.x;
      translateY += newCenter.y - lastCenter.y;

      lastDistance = newDistance;
      lastCenter = newCenter;

      applyTransform();
    } else if (e.touches.length === 1 && isPanning && scale > 1) {
      e.preventDefault();
      translateX = startTranslateX + (e.touches[0].clientX - panStartX);
      translateY = startTranslateY + (e.touches[0].clientY - panStartY);
      applyTransform();
    }
  }

  function onTouchEnd(e: TouchEvent): void {
    // Check for horizontal swipe (not zoomed)
    if (hasMultiple && scale <= 1 && swipeStartTime > 0 && e.changedTouches.length > 0) {
      const dx = e.changedTouches[0].clientX - swipeStartX;
      const dy = e.changedTouches[0].clientY - swipeStartY;
      const elapsed = Date.now() - swipeStartTime;

      // Must be fast enough, far enough horizontally, and more horizontal than vertical
      if (elapsed < 500 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) navigateTo(currentIndex + 1); // swipe left = next
        else navigateTo(currentIndex - 1); // swipe right = prev
      }
      swipeStartTime = 0;
    }

    isPanning = false;
    if (e.touches.length < 2) {
      lastDistance = 0;
    }
    if (scale <= 1) {
      resetTransform();
    }
  }

  overlay.addEventListener('touchstart', onTouchStart, { passive: false });
  overlay.addEventListener('touchmove', onTouchMove, { passive: false });
  overlay.addEventListener('touchend', onTouchEnd, { passive: true });

  // Double-tap to reset zoom
  let lastTapTime = 0;
  img.addEventListener('click', (e) => {
    const now = Date.now();
    if (now - lastTapTime < 300) {
      if (scale > 1) {
        resetTransform();
      } else {
        scale = 2;
        applyTransform();
      }
      e.stopPropagation();
    }
    lastTapTime = now;
  });

  // Navigation button clicks
  prevBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    navigateTo(currentIndex - 1);
  });
  nextBtn?.addEventListener('click', (e) => {
    e.stopPropagation();
    navigateTo(currentIndex + 1);
  });

  // Close handlers
  const close = () => {
    overlay.removeEventListener('touchstart', onTouchStart);
    overlay.removeEventListener('touchmove', onTouchMove);
    overlay.removeEventListener('touchend', onTouchEnd);
    overlay.remove();
    document.removeEventListener('keydown', onKeyDown);
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
    if (hasMultiple && e.key === 'ArrowLeft') navigateTo(currentIndex - 1);
    if (hasMultiple && e.key === 'ArrowRight') navigateTo(currentIndex + 1);
  };

  closeBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    close();
  });

  closeBtn.addEventListener('touchend', (e) => {
    e.preventDefault();
    e.stopPropagation();
    close();
  });

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });

  document.addEventListener('keydown', onKeyDown);
}
